import { Group, Stack, Table, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { CheckCircleIcon, CheckSquareIcon, EyeIcon, SpeakerSlashIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { IAlertSummary } from "../../apis/return-prevention-alert";
import { dateFormat, dateTimeSecFormat } from "../../utils/date";
import { DeliveryRowAction } from "../delivery/action-controls";
import { TableFrame } from "../delivery/atoms";
import { ListState } from "../ui";
import {
  AcknowledgeAlertModal, MuteAlertModal, ResolveAlertModal, useAlertActions,
} from "./alert-action-modals";
import { AlertSeverityBadge } from "./alert-severity-badge";
import { AlertStatusBadge } from "./alert-status-badge";
import { triggerLabel } from "./alert-trigger-label";

interface Props {
  items   : IAlertSummary[];
  onMutate: () => void;
  /** Empty-state copy (the page knows which tab / filter is active). */
  emptyTitle?: string;
  emptyDescription?: string;
  /** Pagination / range footer. */
  footer?: React.ReactNode;
}

/**
 * The backend stores the rate as a PERCENT (threshold-detector.ts: 24.0 = 24%), so it is shown
 * as is. Multiplying by 100 made 24.0 read «2400.0%» (O146).
 */
export function percentText(v: number | null | undefined): string {
  return v == null ? "-" : `${v.toFixed(1)}%`;
}

function MetricCell({ metricValue, thresholdValue }: { metricValue: number | null; thresholdValue: number | null }) {
  return (
    <Text size="sm" ff="monospace" fz="var(--nw-fz-mono)">
      {percentText(metricValue)} / {percentText(thresholdValue)}
    </Text>
  );
}

export function PreventionAlertTable({ items, onMutate, emptyTitle, emptyDescription, footer }: Props) {
  const { t } = useTranslation("returns");
  const navigate = useNavigate();
  const { stateOf } = useAlertActions();
  const [selectedId, setSelectedId] = useState<string>("");

  const [ackOpened, { open: openAck, close: closeAck }]     = useDisclosure(false);
  const [muteOpened, { open: openMute, close: closeMute }]   = useDisclosure(false);
  const [resolveOpened, { open: openResolve, close: closeResolve }] = useDisclosure(false);

  const handleAck = (id: string) => { setSelectedId(id); openAck(); };
  const handleMute = (id: string) => { setSelectedId(id); openMute(); };
  const handleResolve = (id: string) => { setSelectedId(id); openResolve(); };

  return (
    <>
      <TableFrame footer={footer}>
        {items.length === 0 ? (
          <ListState
            state="empty"
            title={emptyTitle ?? t("preventionTable.empty")}
            description={emptyDescription}
          />
        ) : (
          <Table.ScrollContainer minWidth={980}>
            <Table highlightOnHover verticalSpacing="sm" horizontalSpacing="md" styles={{ td: { verticalAlign: "middle" } }}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("preventionTable.actions")}</Table.Th>
                  <Table.Th>{t("preventionTable.product")}</Table.Th>
                  <Table.Th>{t("preventionTable.pattern")}</Table.Th>
                  <Table.Th>{t("preventionTable.metricThreshold")}</Table.Th>
                  <Table.Th>{t("preventionTable.severity")}</Table.Th>
                  <Table.Th>{t("preventionTable.status")}</Table.Th>
                  <Table.Th>{t("preventionTable.createdAt")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {items.map((item) => {
                  const ack = stateOf("acknowledge", item.status);
                  const mute = stateOf("mute", item.status);
                  const resolve = stateOf("resolve", item.status);
                  return (
                    <Table.Tr key={item.id}>
                      {/* Actions: first column, grey subtle icons; unavailable = aria-disabled + reason */}
                      <Table.Td>
                        <Group gap="xs" wrap="nowrap">
                          <DeliveryRowAction
                            label={t("preventionTable.tooltipDetail")}
                            icon={<EyeIcon />}
                            onClick={() => navigate(`/returns/prevention/${item.id}`)}
                          />
                          <DeliveryRowAction
                            label={t("preventionTable.tooltipAck")}
                            icon={<CheckCircleIcon />}
                            state={{ allowed: ack.enabled, reason: ack.reason }}
                            onClick={() => handleAck(item.id)}
                          />
                          <DeliveryRowAction
                            label={t("preventionTable.tooltipMute")}
                            icon={<SpeakerSlashIcon />}
                            state={{ allowed: mute.enabled, reason: mute.reason }}
                            onClick={() => handleMute(item.id)}
                          />
                          <DeliveryRowAction
                            label={t("preventionTable.tooltipResolve")}
                            icon={<CheckSquareIcon />}
                            state={{ allowed: resolve.enabled, reason: resolve.reason }}
                            onClick={() => handleResolve(item.id)}
                          />
                        </Group>
                      </Table.Td>

                      {/* Product + SKU */}
                      <Table.Td maw={280}>
                        <Stack gap={2}>
                          <Text size="sm" lineClamp={2}>{item.productName || "-"}</Text>
                          {item.sku ? (
                            <Text size="sm" c="var(--nw-text-2)" ff="monospace" fz="var(--nw-fz-mono)" translate="no">{item.sku}</Text>
                          ) : null}
                        </Stack>
                      </Table.Td>

                      <Table.Td>
                        <Text size="sm">{triggerLabel(item.triggerType) || "-"}</Text>
                      </Table.Td>

                      <Table.Td>
                        <MetricCell metricValue={item.triggerMetricValue} thresholdValue={item.triggerThresholdValue} />
                      </Table.Td>

                      <Table.Td>
                        <AlertSeverityBadge severity={item.severity} />
                      </Table.Td>

                      {/* Status; a muted alert shows until when (O152) */}
                      <Table.Td>
                        <Stack gap={4} align="flex-start">
                          <AlertStatusBadge status={item.status} />
                          {item.status === "MUTED" && item.mutedUntil ? (
                            <Text size="sm" c="var(--nw-text-2)" ff="monospace" fz="var(--nw-fz-mono)">
                              {t("preventionTable.mutedUntil", { date: dateTimeSecFormat(item.mutedUntil) })}
                            </Text>
                          ) : null}
                        </Stack>
                      </Table.Td>

                      <Table.Td>
                        <Text size="sm" ff="monospace" fz="var(--nw-fz-mono)" style={{ whiteSpace: "nowrap" }}>{dateFormat(item.createdAt)}</Text>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </TableFrame>

      <AcknowledgeAlertModal
        opened={ackOpened}
        onClose={closeAck}
        alertId={selectedId}
        onSuccess={onMutate}
      />
      <MuteAlertModal
        opened={muteOpened}
        onClose={closeMute}
        alertId={selectedId}
        onSuccess={onMutate}
      />
      <ResolveAlertModal
        opened={resolveOpened}
        onClose={closeResolve}
        alertId={selectedId}
        onSuccess={onMutate}
      />
    </>
  );
}
